import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { Button } from '@/components/ui';
import { environment } from '@/config/environment';
import {
  activateServiceWorkerUpdate,
  clearCacheStorage,
  clearServiceWorkerRegistrations,
  refreshServiceWorkerRegistration,
} from '@/services/pwaRegistrationService';
import { SignalEventMethod, signalRService } from '@/services/signalRService';
import {
  hasCookieConsent,
  isInstallPromptDismissed,
  PWA_INSTALL_ANSWER_KEY,
  PWA_INSTALL_NOT_NOW_VALUE,
  setStoredStringValue,
} from '@/utils/pwaStorage';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

type PromptType = 'ios' | 'native';

const isIosDevice = (): boolean => {
  return (
    ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(
      navigator.platform
    ) ||
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  );
};

const isStandaloneMode = (): boolean => {
  const navigatorWithStandalone = navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    navigatorWithStandalone.standalone === true
  );
};

const shouldShowInstallPrompt = (): boolean => {
  return hasCookieConsent() && !isInstallPromptDismissed();
};

export const PwaInstallHandler = () => {
  const { accounts } = useMsal();
  const [isVisible, setIsVisible] = useState(false);
  const [promptType, setPromptType] = useState<PromptType | null>(null);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  const appName = useMemo(() => 'Tankers (UK) Agencies Extranet', []);
  const appUrl = useMemo(
    () => window.location.origin.replace(/^https?:\/\//, '').replace(/\/$/, ''),
    []
  );

  const rejectInstallation = useCallback(() => {
    setStoredStringValue(PWA_INSTALL_ANSWER_KEY, PWA_INSTALL_NOT_NOW_VALUE);
    setIsVisible(false);
    setPromptType(null);
  }, []);

  const checkUpdateAndActivate = useCallback(async (): Promise<boolean> => {
    if (!environment.pwa.enabled || !('serviceWorker' in navigator)) {
      return false;
    }

    await refreshServiceWorkerRegistration();
    return activateServiceWorkerUpdate();
  }, []);

  const handleIosInstallation = useCallback(() => {
    if (!environment.pwa.enabled) {
      return;
    }

    if (!isIosDevice() || isStandaloneMode() || !shouldShowInstallPrompt()) {
      return;
    }

    window.setTimeout(() => {
      setPromptType('ios');
      setIsVisible(true);
    }, 0);
  }, []);

  const addToHomeScreen = useCallback(async () => {
    setIsVisible(false);

    if (!deferredPrompt) {
      rejectInstallation();
      return;
    }

    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;

    if (choice.outcome !== 'accepted') {
      setStoredStringValue(PWA_INSTALL_ANSWER_KEY, PWA_INSTALL_NOT_NOW_VALUE);
    }

    setDeferredPrompt(null);
    setPromptType(null);
  }, [deferredPrompt, rejectInstallation]);

  useEffect(() => {
    void checkUpdateAndActivate();
    handleIosInstallation();
  }, [checkUpdateAndActivate, handleIosInstallation]);

  useEffect(() => {
    if (!environment.pwa.enabled) {
      return;
    }

    const handleBeforeInstallPrompt = (event: Event) => {
      const promptEvent = event as BeforeInstallPromptEvent;
      promptEvent.preventDefault();
      setDeferredPrompt(promptEvent);

      if (shouldShowInstallPrompt()) {
        setPromptType('native');
        setIsVisible(true);
      }
    };

    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setPromptType(null);
      setIsVisible(false);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  useEffect(() => {
    if (accounts.length > 0) {
      void signalRService.initialize();
    }
  }, [accounts.length]);

  useEffect(() => {
    const unsubscribeForceUpdate = signalRService.subscribe<{ isForce?: boolean }>(
      SignalEventMethod.FORCE_SW_UPDATE,
      async ({ isForce }) => {
        try {
          if (isForce) {
            await clearServiceWorkerRegistrations();
            await clearCacheStorage();
            localStorage.clear();
            await signalRService.log('SWUpdate: Force update and clear site data successful!');
            window.location.reload();
            return;
          }

          let message = 'SWUpdate: Successful!';

          if (!environment.pwa.enabled || !('serviceWorker' in navigator)) {
            message = 'SWUpdate: Failed, service worker is disabled.';
          } else {
            const hasUpdate = await checkUpdateAndActivate();
            if (hasUpdate === false) {
              message = 'SWUpdate: No changes detected!';
            }
          }

          await signalRService.log(message);
        } catch (error) {
          await signalRService.log('SWUpdate: Error: ' + String(error));
        }
      }
    );

    return () => {
      unsubscribeForceUpdate();
    };
  }, [checkUpdateAndActivate]);

  if (!isVisible || !promptType) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-1/2 z-50 w-[90vw] max-w-md -translate-x-1/2 rounded-lg border border-gray-200 bg-white p-4 shadow-lg dark:border-dark-700 dark:bg-dark-800">
      {promptType === 'ios' ? (
        <div className="flex flex-col gap-3">
          <div className="flex items-start gap-3">
            <img className="h-12 w-12" src="/logo.png" alt="TIX App" />
            <p className="text-sm text-gray-700 dark:text-gray-200">
              Install this web app on your iPhone: tap{' '}
              <img
                alt="safari_share"
                src="/assets/images/safari-share-icon.png"
                className="inline h-4 w-4 align-middle"
              />{' '}
              and then <strong>Add to Home Screen</strong>
            </p>
          </div>
          <div className="flex justify-end">
            <Button variant="ghost" size="sm" onClick={rejectInstallation}>
              Maybe later
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <h5 className="text-base font-semibold text-gray-900 dark:text-white">
            Install/add to home screen
          </h5>

          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="TIX App" className="h-12 w-12" />
            <div>
              <h6 className="text-sm font-semibold text-gray-900 dark:text-white">{appName}</h6>
              <span className="text-xs text-gray-500 dark:text-gray-400">{appUrl}</span>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={rejectInstallation}>
              CANCEL
            </Button>
            <Button variant="ghost" size="sm" onClick={addToHomeScreen}>
              ADD
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
